import "core-js/modules/es6.object.assign.js";
import "core-js/modules/es6.symbol.js";
import "core-js/modules/web.dom.iterable.js";
import "core-js/modules/es6.array.iterator.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.object.keys.js";

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from 'react';
import classNames from 'classnames';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { ButtonSpinnerAnimation } from './ButtonSpinnerAnimation';
import styles from './Button.module.less';

var NOOP = function NOOP() {};

var inferComponent = function inferComponent(_ref) {
  var href = _ref.href;
  return href ? 'a' : 'button';
};

var preventDefaultAndStopPropagation = function preventDefaultAndStopPropagation(e) {
  e.preventDefault();
  e.stopPropagation();
};

var getDisabledProps = function getDisabledProps(Component, disabled) {
  if (!disabled) {
    return {};
  }

  if (Component === 'a') {
    return {
      tabIndex: -1,
      'aria-disabled': true,
      onMouseDown: preventDefaultAndStopPropagation,
      onClick: preventDefaultAndStopPropagation
    };
  }

  return {
    disabled: true
  };
};

var getTypeProps = function getTypeProps(Component) {
  // Should use type=button when using a <button> component, but not needed
  // when using <a>. This can be overridden by passing a `type` prop.
  if (Component === 'button') {
    return {
      type: 'button'
    };
  }

  return {};
};

export var Button = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames;

  var block = props.block,
      component = props.component,
      children = props.children,
      className = props.className,
      disabled = props.disabled,
      loading = props.loading,
      loadingMessage = props.loadingMessage,
      primary = props.primary,
      tertiary = props.tertiary,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceActiveStyling = props.dangerouslyForceActiveStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      otherProps = _objectWithoutProperties(props, ["block", "component", "children", "className", "disabled", "loading", "loadingMessage", "primary", "tertiary", "dangerouslyForceFocusStyling", "dangerouslyForceActiveStyling", "dangerouslyForceHoverStyling"]);

  var Component = component || inferComponent(otherProps);
  return /*#__PURE__*/React.createElement(Component // bind a noop ontouchstart handler
  // so that ios will style :active states
  , _extends({
    onTouchStart: NOOP,
    className: classNames(className, styles.button, Component === 'a' && styles['link-button'], (_classNames = {}, _defineProperty(_classNames, styles.loading, loading), _defineProperty(_classNames, styles.primary, !disabled && primary), _defineProperty(_classNames, styles.secondary, !disabled && !primary && !tertiary), _defineProperty(_classNames, styles.tertiary, !disabled && !primary && tertiary), _defineProperty(_classNames, styles['primary-disabled'], disabled && primary), _defineProperty(_classNames, styles['secondary-disabled'], disabled && !primary && !tertiary), _defineProperty(_classNames, styles['tertiary-disabled'], disabled && !primary && tertiary), _defineProperty(_classNames, styles.block, block), _defineProperty(_classNames, styles['force-focus'], dangerouslyForceFocusStyling), _defineProperty(_classNames, styles['force-hover'], dangerouslyForceHoverStyling), _defineProperty(_classNames, styles['force-active'], dangerouslyForceActiveStyling), _classNames)),
    ref: ref
  }, getTypeProps(Component), otherProps, getDisabledProps(Component, disabled)), /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, children), /*#__PURE__*/React.createElement("div", {
    className: styles['loading-container']
  }, /*#__PURE__*/React.createElement(ButtonSpinnerAnimation, {
    show: loading
  }, /*#__PURE__*/React.createElement(LoadingSpinner, {
    color: primary ? 'green-light' : 'green',
    loadingMessage: loadingMessage // https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA22
    // This component uses the "status" role to notify screen readers that
    // the page has updated with information about the application's status.
    // This is the same as using aria-live="polite", which will not
    // interrupt the screen reader's current task.
    ,
    role: "status",
    size: 24
  }))));
});
Button.displayName = 'Button';
Button.defaultProps = {
  block: false,
  className: null,
  component: null,
  disabled: false,
  primary: false,
  tertiary: false,
  loading: false,
  loadingMessage: 'Loading',
  href: undefined,
  // Props used for testing to force visual states
  dangerouslyForceHoverStyling: false,
  dangerouslyForceFocusStyling: false,
  dangerouslyForceActiveStyling: false
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require -- directive added automatically by Shepherd migration
  var PropTypes = require('prop-types');

  Button.propTypes = {
    children: PropTypes.node.isRequired,

    /** Changes the button to `display: block`, making it go full width. */
    block: PropTypes.bool,

    /** Applies a CSS classname to the component. */
    className: PropTypes.string,

    /**
     * Allows you to provide a React component or other HTML element,
     * overriding the default `a` and `button` options.
     */
    component: PropTypes.string,

    /** Renders a button as disabled, making it non-interactable. */
    disabled: PropTypes.bool,

    /**
     * Url to navigate to when clicked
     */
    href: PropTypes.string,

    /** Renders a button loading visual state. */
    loading: PropTypes.bool,

    /** Message to be read by the screen reader when loading state is active. */
    loadingMessage: PropTypes.string,

    /** Renders a button with the highest visual affordances. */
    primary: PropTypes.bool,

    /** Renders a button with the tertiary visual affordances. */
    tertiary: PropTypes.bool,

    /** Force the component to use its hover styles. */
    dangerouslyForceHoverStyling: PropTypes.bool,

    /** Force the component to use its focus styles. */
    dangerouslyForceFocusStyling: PropTypes.bool,

    /** Force the component to use its active styles. */
    dangerouslyForceActiveStyling: PropTypes.bool
  };
}